import { useMutation } from '@apollo/client'
import styled from '@emotion/styled'
import { UntitledIcon } from '@faceup/icons'
import { ulMinusCircle } from '@faceup/icons/ulMinusCircle'
import { ulPlus } from '@faceup/icons/ulPlus'
import { Link } from '@faceup/router'
import { Form } from '@faceup/ui'
import { Button, Input, useMessage, useNotification } from '@faceup/ui-base'
import { Country, isEmail, Plan } from '@faceup/utils'
import { type MouseEvent, memo, useState } from 'react'
import { graphql } from '../__generated__'

const mutations = {
  SendReportEmailsMutation: graphql(`
    mutation SendReportEmailsMutation($input: SendReportEmailsInput!) {
      sendReportEmails(input: $input) {
        editedCase {
          id
          ... on CompanyReport {
            emailsSent
          }
          organizationalUnit {
            id
            organizationalUnitName
            country
            billing {
              id
              plan
            }

            isVerified
            isPendingVerification
          }
        }
      }
    }
  `),
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

type Props = {
  id: string
  closed: boolean | null
  emailsSent: boolean | null
  organizationalUnit: {
    id?: string | null
    name?: string | null
    isVerified: boolean | null
    isPendingVerification: boolean | null
    country: Country | null
    billing: {
      plan: Plan | null
    } | null
  }
}

const NonRegisteredEmails = memo(({ organizationalUnit, closed, emailsSent, id }: Props) => {
  const [emails, setEmails] = useState<string[]>([''])
  const message = useMessage()
  const notification = useNotification()

  const [sendReportEmails, { loading }] = useMutation(mutations.SendReportEmailsMutation, {
    onCompleted: () => message.success('E-mails sent'),
  })

  // eg do not wait for registration
  const showForm =
    organizationalUnit?.country === Country.Cz &&
    organizationalUnit?.billing?.plan === Plan.Unregistered &&
    !closed

  const validateForm = () => {
    const wrongEmails = emails.filter(email => !isEmail(email))
    if (wrongEmails.length) {
      notification.error({
        message: 'Following emails are invalid',
        description: wrongEmails.join(', '),
      })
      return false
    }

    return true
  }

  if (organizationalUnit?.isPendingVerification) {
    return (
      <>
        Institution{' '}
        <Link to={routes => routes.institution({ id: organizationalUnit.id ?? '' })}>
          {organizationalUnit?.name}
        </Link>{' '}
        is pending verification. Verify it please first
      </>
    )
  }

  if (!showForm) {
    return null
  }

  return (
    <>
      <NonRegisteredSchoolEmailsContainer>
        <NonRegisteredSchoolEmailsAside>
          <NonRegisteredSchoolEmailsHeading>E-mail</NonRegisteredSchoolEmailsHeading>
        </NonRegisteredSchoolEmailsAside>

        <NonRegistredSchoolEmailsContent>
          {emails.map((email, key) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Email list with dynamic add/remove, index is stable identifier
            <Form.Item key={key} {...formItemLayout} withAsterisk>
              <EmailInput
                type='email'
                autoComplete='email'
                placeholder='Add e-mail'
                value={email}
                onChange={({ target: { value } }) =>
                  setEmails(emails.map((e, idx) => (idx === key ? value : e)))
                }
              />
              <DynamicDeleteIcon
                icon={ulMinusCircle}
                onClick={() => setEmails(emails.filter((_, idx) => idx !== key))}
              />
            </Form.Item>
          ))}

          <NonRegistredSchoolEmailsContent>
            <Button block onClick={() => setEmails([...emails, ''])}>
              <UntitledIcon icon={ulPlus} />
              Add e-mail
            </Button>
          </NonRegistredSchoolEmailsContent>
        </NonRegistredSchoolEmailsContent>
      </NonRegisteredSchoolEmailsContainer>
      <div className='flex flex-row justify-end mt-5'>
        <Button
          loading={loading}
          disabled={emails?.length === 0}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()

            if (validateForm()) {
              setEmails([])
              sendReportEmails({
                variables: {
                  input: {
                    caseId: id,
                    emailsForUnregisteredInstitution: emails,
                  },
                },
              })
            }
          }}
        >
          Send e-mails
          {emailsSent ? (
            <>
              &nbsp;AGAIN <i>(e-mails already sent)</i>
            </>
          ) : null}
        </Button>
      </div>
    </>
  )
})

const NonRegisteredSchoolEmailsContainer = styled.div`
  display: flex;
  width: 100%;
`

const NonRegistredSchoolEmailsContent = styled.div`
  width: 90%;
`

const NonRegisteredSchoolEmailsAside = styled.div`
  width: 10%;
`

const NonRegisteredSchoolEmailsHeading = styled.h4`
  margin-top: 10px;
`

const DynamicDeleteIcon = styled(UntitledIcon)`
  height: 100%;
`

const EmailInput = styled(Input)`
  width: 90%;
  margin-right: 8px;
`

NonRegisteredEmails.displayName = NonRegisteredEmails.name

export default NonRegisteredEmails
